import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import { Raumstandhinweis } from './Raumstandhinweis';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, PropertyGrid, StatusBadge, WertEinheit,
  type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Beleuchtungsnachweis je Raum nach DIN EN 12464-1 (Paket V-E4).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS DIESE SEITE ZEIGT — UND WAS SIE NICHT ZEIGT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sie zeigt je Raum den Wartungswert Ēm, den die Norm fordert, und die
 * mittlere Beleuchtungsstärke, die die im Modell stehenden Leuchten nach dem
 * Wirkungsgradverfahren liefern.
 *
 * Sie zeigt NICHT die Verteilung des Lichts im Raum. Das
 * Wirkungsgradverfahren liefert einen MITTELWERT über die Grundfläche; die
 * Gleichmäßigkeit U0 und die Blendungsbegrenzung UGR, die die Norm
 * gleichrangig fordert, brauchen eine Punktrechnung (Relux, DIALux). Deshalb
 * steht der Vorbehalt oben und nicht in einer Fußnote — wer ihn erst unter
 * der Tabelle liest, hat die Zahlen darüber schon geglaubt.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DER AUFBAU FOLGT DEM MELDERBEDARF
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Kennzahlen, Filterleiste, Tabelle, darunter die gewählte Zeile mit ihrer
 * Begründung — dasselbe Muster wie bei „Anforderung TT-Platten Rauchmelder"
 * und aus demselben Grund: Es ist dieselbe Frage (reicht, was da ist?) an
 * derselben Menge (den Räumen des Raumbuchs).
 *
 * Gefiltert wird auf dem SERVER (Befund, Geschoss) und gesucht im BROWSER:
 * Der Befundfilter verkleinert die Menge deutlich, die Suche über Nummer und
 * Name dagegen nicht — eine Abfrage je Tastendruck wäre verschwendet.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * UMZUG AUF DIE MUSTERBIBLIOTHEK (Block U, Paket U6b)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `PageHeader`, `KpiCard`, `DataTable`, `StatusBadge`, `PropertyGrid`,
 * `LoadingState`/`EmptyState`/`ErrorState`. Der Befund steht jetzt als
 * `StatusBadge` mit Zeichen UND Farbe in der Tabelle; vorher war er
 * farbiger Text, den niemand mit Rotschwäche unterscheiden konnte.
 */
export function ElektroBeleuchtung() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [d, setD] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const [befund, setBefund] = useState('');
  const [geschoss, setGeschoss] = useState('');
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [zeigeKatalog, setZeigeKatalog] = useState(false);
  const [zeigeTypen, setZeigeTypen] = useState(false);

  const laden = useCallback(() => {
    if (!projekt) return;
    setD(null);
    setFehler(null);

    api.elektroBeleuchtung(projekt.id, {
      befund: befund || undefined,
      geschoss: geschoss || undefined,
    })
      .then((x) => { setD(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id, befund, geschoss]);

  useEffect(() => { laden(); }, [laden]);

  // WICHTIG (Block U, Paket U6b): Diese Felder MUESSEN gemerkt werden.
  // Vorher stand hier `const zeilen: any[] = d?.zeilen ?? []` - ein neues,
  // leeres Feld bei JEDEM Durchlauf, solange `d` null ist oder die Antwort
  // kein `zeilen` traegt. Der Effekt weiter unten haengt `zeilen` in seiner
  // Abhaengigkeitsliste und ruft `setGeschosse([...])`; React sah jedes Mal
  // eine Aenderung, rendert neu, der Effekt lief wieder: rund 500 Warnungen
  // "Maximum update depth exceeded" je Seitenaufruf. Mit `useMemo` bleibt
  // die Kennung des leeren Feldes ueber alle Durchlaeufe dieselbe.
  const zeilen: any[] = useMemo(() => d?.zeilen ?? [], [d]);
  const lauf = d?.lauf ?? null;
  const hinweise: string[] = useMemo(() => d?.hinweise ?? [], [d]);
  const anforderungen: any[] = useMemo(() => d?.anforderungen ?? [], [d]);
  const annahmen: any[] = useMemo(() => d?.annahmen ?? [], [d]);
  const typen: any[] = useMemo(() => d?.typen ?? [], [d]);

  // Die Geschosse kommen aus dem Lauf und nicht aus den gefilterten Zeilen:
  // Sonst verschwände die Auswahl, sobald man ein Geschoss gewählt hat, in
  // dem der aktuelle Befundfilter nichts findet.
  const [geschosse, setGeschosse] = useState<string[]>([]);

  useEffect(() => {
    if (befund || geschoss) return;

    setGeschosse([...new Set(zeilen
      .map((z) => z.geschoss)
      .filter((g: string | null): g is string => !!g))].sort());
  }, [zeilen, befund, geschoss]);

  const raum = useMemo(
    () => zeilen.find((z) => z.id === gewaehlt) ?? null,
    [zeilen, gewaehlt]);

  const zahl = (w: number | null | undefined, n = 0) =>
    w === null || w === undefined
      ? '–'
      : w.toLocaleString('de-DE',
          { minimumFractionDigits: n, maximumFractionDigits: n });

  const befundZustand = (b: string): Zustand =>
    (b === 'erfuellt' ? 'gut'
      : b === 'unterversorgt' ? 'fehler'
        : b === 'ueberversorgt' ? 'warnung'
          : 'neutral');

  const befundtext = (b: string) => ({
    erfuellt: 'erfüllt',
    unterversorgt: 'unterversorgt',
    ueberversorgt: 'überversorgt',
    keine_leuchten: 'keine Leuchte',
    typ_ohne_kennwert: 'Typ ohne Kennwert',
    anforderung_offen: 'Anforderung offen',
    ohne_geometrie: 'ohne Geometrie',
  } as Record<string, string>)[b] ?? b;

  /* --- Spalten der Nachweistabelle ------------------------------------- */
  const spalten: Spalte<any>[] = useMemo(() => [
    { schluessel: 'geschoss', titel: 'Geschoss', breite: '110px', wert: (z) => z.geschoss },
    {
      schluessel: 'raum', titel: 'Raum', immerSichtbar: true,
      wert: (z) => `${z.raumnummer ?? ''} ${z.raumname ?? ''}`.trim(),
    },
    {
      schluessel: 'anforderung', titel: 'Anforderung',
      wert: (z) => z.anforderungBezeichnung,
      inhalt: (z) => (
        <span className="schwach">{z.anforderungBezeichnung ?? '–'}</span>
      ),
    },
    {
      schluessel: 'flaeche', titel: 'Fläche', art: 'zahl', einheit: 'm²',
      nachkomma: 1, wert: (z) => z.flaecheM2, breite: '100px',
    },
    {
      schluessel: 'raumindex', titel: 'k', art: 'zahl', nachkomma: 2,
      titelHinweis: 'Raumindex; * = auf den Rand der Wirkungsgradtabelle gekappt',
      wert: (z) => z.raumindex, breite: '90px',
      inhalt: (z) => (
        <>{zahl(z.raumindex, 2)}{z.raumindexGekappt ? ' *' : ''}</>
      ),
    },
    {
      schluessel: 'leuchten', titel: 'Leuchten', art: 'zahl', breite: '130px',
      wert: (z) => z.leuchtenAnzahl,
      inhalt: (z) => (
        <>
          {zahl(z.leuchtenAnzahl)}
          {z.leuchtenMitKennwert < z.leuchtenAnzahl
            ? ` (${zahl(z.leuchtenMitKennwert)} m. Wert)` : ''}
        </>
      ),
    },
    {
      schluessel: 'soll', titel: 'Soll', art: 'zahl', einheit: 'lx',
      wert: (z) => z.wartungswertSollLx, breite: '90px',
    },
    {
      schluessel: 'ist', titel: 'Ist', art: 'zahl', einheit: 'lx',
      wert: (z) => z.beleuchtungsstaerkeIstLx, breite: '90px',
    },
    {
      schluessel: 'erfuellung', titel: 'Erfüllung', art: 'zahl', einheit: '%',
      wert: (z) => z.erfuellungsgradProzent, breite: '100px',
    },
    {
      schluessel: 'befund', titel: 'Befund', art: 'status', breite: '160px',
      wert: (z) => befundtext(z.befund),
      inhalt: (z) => (
        <StatusBadge
          zustand={befundZustand(z.befund)}
          text={befundtext(z.befund)}
          klein
        />
      ),
    },
  ], []);

  const anforderungsspalten: Spalte<any>[] = useMemo(() => [
    { schluessel: 'bezeichnung', titel: 'Anforderung', immerSichtbar: true, wert: (a) => a.bezeichnung },
    { schluessel: 'em', titel: 'Ēm', art: 'zahl', einheit: 'lx', wert: (a) => a.wartungswertLx },
    { schluessel: 'ugr', titel: 'UGR', art: 'zahl', wert: (a) => a.ugrGrenze },
    { schluessel: 'ra', titel: 'Ra', art: 'zahl', wert: (a) => a.raMindest },
    { schluessel: 'u0', titel: 'U0', art: 'zahl', nachkomma: 2, wert: (a) => a.gleichmaessigkeitU0 },
    {
      schluessel: 'nutzebene', titel: 'Nutzebene', art: 'zahl', einheit: 'm',
      nachkomma: 2, wert: (a) => a.nutzebeneM,
    },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (a) => a.herkunft },
    {
      schluessel: 'normzitat', titel: 'Normzitat', wert: (a) => a.normzitat,
      inhalt: (a) => <span className="schwach">{a.normzitat ?? '–'}</span>,
    },
    {
      schluessel: 'geprueft', titel: 'geprüft', art: 'status', breite: '150px',
      wert: (a) => (a.geprueft ? 'geprüft' : 'nicht geprüft'),
      inhalt: (a) => (a.geprueft
        ? <StatusBadge zustand="gut" text="geprüft" klein />
        : <StatusBadge zustand="warnung" text="nicht geprüft" klein />),
    },
  ], []);

  const annahmenspalten: Spalte<any>[] = useMemo(() => [
    {
      schluessel: 'schluessel', titel: 'Schlüssel', immerSichtbar: true,
      wert: (a) => a.schluessel,
      inhalt: (a) => <code>{a.schluessel}</code>,
    },
    {
      schluessel: 'wert', titel: 'Wert', ausrichtung: 'rechts',
      wert: (a) => (a.zahlwert ?? a.wert),
      inhalt: (a) => (a.zahlwert !== null && a.zahlwert !== undefined
        ? <WertEinheit wert={a.zahlwert} nachkomma={2} einheit={a.einheit ?? undefined} rechts />
        : <>{a.wert ?? '–'}</>),
    },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (a) => a.herkunft },
    {
      schluessel: 'begruendung', titel: 'Begründung',
      wert: (a) => a.begruendung ?? a.normbezug,
      inhalt: (a) => (
        <span className="schwach">{a.begruendung ?? a.normbezug ?? '–'}</span>
      ),
    },
  ], []);

  const typenspalten: Spalte<any>[] = useMemo(() => [
    { schluessel: 'familie', titel: 'Familie', immerSichtbar: true, wert: (t) => t.familie },
    { schluessel: 'typ', titel: 'Typ', wert: (t) => t.typ },
    {
      schluessel: 'bauteilart', titel: 'Art', wert: (t) => t.bauteilart,
      inhalt: (t) => <span className="schwach">{t.bauteilart}</span>,
    },
    {
      schluessel: 'lichtstrom', titel: 'Lichtstrom', art: 'zahl', einheit: 'lm',
      wert: (t) => t.lichtstromLm,
      inhalt: (t) => (t.lichtstromLm
        ? <WertEinheit wert={t.lichtstromLm} rechts />
        : <StatusBadge zustand="fehler" text="fehlt" klein />),
    },
    { schluessel: 'leistung', titel: 'Leistung', art: 'zahl', einheit: 'W', nachkomma: 1, wert: (t) => t.leistungW },
    { schluessel: 'ausbeute', titel: 'Lichtausbeute', art: 'zahl', einheit: 'lm/W', wert: (t) => t.lichtausbeuteLmW },
    { schluessel: 'ra', titel: 'Ra', art: 'zahl', wert: (t) => t.farbwiedergabeRa },
    { schluessel: 'ugr', titel: 'UGR', art: 'zahl', wert: (t) => t.ugrKatalog },
    { schluessel: 'anzahl', titel: 'Stück', art: 'zahl', wert: (t) => t.anzahl },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (t) => t.herkunft },
  ], []);

  /* --- Zustände der Seite ----------------------------------------------- */
  if (fehler) {
    return (
      <>
        <PageHeader
          titel="Beleuchtung — Nachweis je Raum"
          beschreibung="Nachweis der Beleuchtungsanforderungen nach DIN EN 12464-1 auf Basis des aktuellen Revit-Modells."
        />
        <ErrorState
          meldung="Der Beleuchtungsnachweis konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!projekt || !d) {
    return (
      <>
        <PageHeader
          titel="Beleuchtung — Nachweis je Raum"
          beschreibung="Nachweis der Beleuchtungsanforderungen nach DIN EN 12464-1 auf Basis des aktuellen Revit-Modells."
        />
        <LoadingState text="Beleuchtungsnachweis wird geladen …" zeilen={8} spalten={6} />
      </>
    );
  }

  if (!lauf) {
    return (
      <>
        <PageHeader
          titel="Beleuchtung — Nachweis je Raum"
          beschreibung="Nachweis der Beleuchtungsanforderungen nach DIN EN 12464-1 auf Basis des aktuellen Revit-Modells."
        />
        <EmptyState
          titel="Für dieses Modell wurde noch keine Beleuchtungsrechnung durchgeführt."
          erklaerung={d.meldung
            ?? 'Der Nachweis entsteht aus einem Lauf über die Räume des Raumbuchs '
            + 'und die Leuchten des aufgenommenen Modells.'}
          aktion={(
            <button className="m-knopf m-knopf-haupt" onClick={laden}>
              Erneut nachsehen
            </button>
          )}
        />
      </>
    );
  }

  const ungeprueft = anforderungen.filter((a) => !a.geprueft).length;

  return (
    <>
      <PageHeader
        titel="Beleuchtung — Nachweis je Raum"
        beschreibung="Nachweis der Beleuchtungsanforderungen nach DIN EN 12464-1 auf Basis des aktuellen Revit-Modells."
        meta={(
          <>
            Gerechnet am {new Date(lauf.gerechnetAm).toLocaleString('de-DE')}
            {lauf.gerechnetVon ? ` von ${lauf.gerechnetVon}` : ''}
          </>
        )}
        aktionen={(
          <>
            <button
              className="m-knopf m-knopf-haupt"
              onClick={() => api.elektroBeleuchtungPdf(projekt.id)}
            >
              Nachweis ELT-BEL als PDF
            </button>
            <button className="m-knopf" onClick={() => setZeigeKatalog((v) => !v)}>
              {zeigeKatalog ? 'Normkatalog ausblenden' : 'Normkatalog und Annahmen'}
            </button>
            <button className="m-knopf" onClick={() => setZeigeTypen((v) => !v)}>
              {zeigeTypen ? 'Leuchtentypen ausblenden' : `Leuchtentypen (${typen.length})`}
            </button>
          </>
        )}
      />
      <Raumstandhinweis api={api} projektId={projekt.id} rechnungen={['beleuchtung']} />

      {/* Der Vorbehalt steht OBEN. Ein Mittelwert über die Grundfläche sieht
          aus wie ein Nachweis und ist keiner, solange U0 und UGR fehlen. */}
      <div className="hinweis warnung" style={{ display: 'block', maxWidth: 860 }}>
        <strong>Mittelwert, kein Punktnachweis.</strong> Das
        Wirkungsgradverfahren liefert die mittlere Beleuchtungsstärke über die
        Grundfläche. Die Gleichmäßigkeit <code>U0</code> wird nicht gerechnet,
        das <code>UGR</code> nur als Katalogaussage des Leuchtentyps gegen die
        Normgrenze gehalten. Für Arbeitsplätze ersetzt das keine
        Punktrechnung mit Lichtstärkeverteilungskurven.
        {ungeprueft > 0 && (
          <> {ungeprueft} von {anforderungen.length} Anforderungszeilen sind
          <strong> nicht am Normexemplar geprüft</strong> — solange das so
          ist, ist jede Zahl hier ein Vorabzug.</>
        )}
      </div>

      {/* --- Kennzahlen ---------------------------------------------------- */}
      <KpiReihe>
        <KpiCard titel="Räume geprüft" wert={lauf.raeumeGesamt} fussnote="DIN EN 12464-1" />
        <KpiCard
          titel="Ēm erfüllt" wert={lauf.erfuellt}
          vergleich={`von ${zahl(lauf.raeumeGesamt)} Räumen`}
          zustand="gut" zustandText="erfüllt"
        />
        <KpiCard
          titel="unterversorgt" wert={lauf.unterversorgt}
          zustand={lauf.unterversorgt > 0 ? 'fehler' : 'gut'}
          zustandText={lauf.unterversorgt > 0 ? 'unter dem Wartungswert' : 'keiner'}
        />
        <KpiCard titel="überversorgt" wert={lauf.ueberversorgt} />
        <KpiCard titel="ohne Leuchte" wert={lauf.ohneLeuchten} />
        <KpiCard
          titel="Leuchtentypen mit Lichtstrom"
          wert={lauf.typenGesamt - lauf.typenOhneKennwert}
          vergleich={`von ${zahl(lauf.typenGesamt)} Typen`}
          zustand={lauf.typenOhneKennwert > 0 ? 'warnung' : 'gut'}
          zustandText={lauf.typenOhneKennwert > 0
            ? `${zahl(lauf.typenOhneKennwert)} ohne Kennwert` : 'vollständig'}
        />
      </KpiReihe>

      {/* --- Die Hinweise des Laufs ---------------------------------------- */}
      {hinweise.length > 0 && (
        <ContentSection titel="Was der Lauf zu seinen eigenen Zahlen sagt">
          <ul className="schwach" style={{ maxWidth: 860 }}>
            {hinweise.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </ContentSection>
      )}

      {/* --- Der Normkatalog und die Annahmen ------------------------------ */}
      {zeigeKatalog && (
        <>
          <ContentSection titel="Anforderungen nach DIN EN 12464-1" nackt>
            <DataTable
              name="Anforderungen"
              spalten={anforderungsspalten}
              zeilen={anforderungen}
              schluessel={(a) => String(a.schluessel)}
              ausfuhr
              ausfuhrDateiname="beleuchtung-anforderungen"
              leer={(
                <EmptyState
                  titel="Der Normkatalog ist für dieses Projekt leer."
                  erklaerung="Ohne Anforderungszeilen lässt sich kein Sollwert je Raum zuordnen."
                />
              )}
            />
          </ContentSection>

          <ContentSection titel="Rechenannahmen" nackt>
            <DataTable
              name="Annahmen"
              spalten={annahmenspalten}
              zeilen={annahmen}
              schluessel={(a) => String(a.schluessel)}
              ausfuhr
              ausfuhrDateiname="beleuchtung-annahmen"
              leer={(
                <EmptyState
                  titel="Für diesen Lauf sind keine Rechenannahmen hinterlegt."
                  erklaerung="Annahmen halten fest, welcher Wert ohne Messdaten angesetzt wurde."
                />
              )}
            />
          </ContentSection>
        </>
      )}

      {/* --- Die Leuchtentypen --------------------------------------------- */}
      {zeigeTypen && (
        <ContentSection titel="Leuchtentypen" nackt>
          <DataTable
            name="Leuchtentypen"
            spalten={typenspalten}
            zeilen={typen}
            schluessel={(t) => `${t.familie}|${t.typ}`}
            ausfuhr
            ausfuhrDateiname="leuchtentypen"
            leer={(
              <EmptyState
                titel="Im Modell steht noch kein Leuchtentyp."
                erklaerung="Die Typen entstehen aus der Modellaufnahme des Gewerks Elektro."
              />
            )}
          />
        </ContentSection>
      )}

      {/* --- Der Nachweis je Raum ------------------------------------------ */}
      <DataTable
        name="Nachweis je Raum"
        beschreibung="Wartungswert Ēm, Blendungsgrenze UGR und Farbwiedergabe Ra je Raum; der Istwert nach dem Wirkungsgradverfahren aus den Leuchten des Modells."
        spalten={spalten}
        zeilen={zeilen}
        schluessel={(z) => String(z.id)}
        suchePlatzhalter="Raum oder Anforderung"
        ausfuhr
        ausfuhrDateiname="beleuchtungsnachweis"
        beiZeileKlick={(z) => setGewaehlt(z.id === gewaehlt ? null : z.id)}
        aktiveZeile={gewaehlt ?? undefined}
        werkzeuge={(
          <>
            <select
              value={befund}
              onChange={(e) => setBefund(e.target.value)}
              aria-label="Befund"
              style={{ width: 'auto' }}
            >
              <option value="">alle Befunde</option>
              <option value="erfuellt">erfüllt</option>
              <option value="unterversorgt">unterversorgt</option>
              <option value="ueberversorgt">überversorgt</option>
              <option value="keine_leuchten">keine Leuchte</option>
              <option value="typ_ohne_kennwert">Typ ohne Kennwert</option>
              <option value="anforderung_offen">Anforderung offen</option>
              <option value="ohne_geometrie">ohne Geometrie</option>
            </select>

            <select
              value={geschoss}
              onChange={(e) => setGeschoss(e.target.value)}
              aria-label="Geschoss"
              style={{ width: 'auto' }}
            >
              <option value="">alle Geschosse</option>
              {geschosse.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </>
        )}
        leer={(
          <EmptyState
            titel={befund || geschoss
              ? 'Kein Raum passt zu diesem Filter.'
              : 'Der Lauf hat keinen Raum bewertet.'}
            erklaerung={befund || geschoss
              ? 'Befund- und Geschossfilter wirken auf dem Server und schränken die Menge ein.'
              : 'Ohne Räume im Raumbuch gibt es nichts, wogegen die Leuchten zu halten wären.'}
            aktion={(befund || geschoss) ? (
              <button
                className="m-knopf"
                onClick={() => { setBefund(''); setGeschoss(''); }}
              >
                Filter aufheben
              </button>
            ) : undefined}
          />
        )}
      />

      <p className="schwach" style={{ marginTop: 8, maxWidth: 860 }}>
        * Der Raumindex liegt ausserhalb der Wirkungsgradtabelle und wurde
        auf deren Rand gekappt. Unterhalb ist η_R danach zu gross und die
        Beleuchtungsstärke zu hoch, oberhalb umgekehrt.
      </p>

      {/* --- Die gewählte Zeile -------------------------------------------- */}
      {raum && (
        <ContentSection
          titel={`${raum.raumnummer} ${raum.raumname}`}
          beschreibung={raum.befundtext}
          aktionen={(
            <button className="m-knopf m-knopf-still" onClick={() => setGewaehlt(null)}>
              Schließen
            </button>
          )}
        >
          <PropertyGrid
            spalten={2}
            eintraege={[
              {
                beschriftung: 'Anforderung',
                wert: (
                  <>
                    {raum.anforderungBezeichnung ?? '–'}
                    {raum.anforderungHerkunft ? ` (${raum.anforderungHerkunft})` : ''}
                    {raum.anforderungNormzitat
                      ? <div className="schwach">{raum.anforderungNormzitat}</div>
                      : null}
                  </>
                ),
                breit: true,
              },
              { beschriftung: 'zugeordnet über', wert: raum.zuordnungsregel ?? null },
              { beschriftung: 'Nutzung im Raumbuch', wert: raum.nutzung ?? null },

              { beschriftung: 'Fläche', wert: raum.flaecheM2, einheit: 'm²', nachkomma: 1, herkunft: 'modell' },
              { beschriftung: 'Umfang', wert: raum.umfangM, einheit: 'm', nachkomma: 1, herkunft: 'modell' },
              {
                beschriftung: 'Raumhöhe', wert: raum.raumhoeheM, einheit: 'm', nachkomma: 2,
                herkunft: 'modell', hinweis: raum.hoeheHerkunft ?? undefined,
              },
              { beschriftung: 'Nutzebene', wert: raum.nutzebeneM, einheit: 'm', nachkomma: 2 },
              { beschriftung: 'Lichtpunkthöhe', wert: raum.lichtpunkthoeheM, einheit: 'm', nachkomma: 2 },

              { beschriftung: 'Raumindex k', wert: raum.raumindex, nachkomma: 2 },
              { beschriftung: 'Raumwirkungsgrad η_R', wert: raum.raumwirkungsgradEtaR, nachkomma: 2 },
              { beschriftung: 'LOR-Faktor', wert: raum.lorFaktor, nachkomma: 2 },
              { beschriftung: 'Wartungsfaktor', wert: raum.wartungsfaktor, nachkomma: 2 },

              { beschriftung: 'Leuchten', wert: raum.leuchtenAnzahl, einheit: 'Stück', herkunft: 'modell' },
              { beschriftung: 'Lichtstrom gesamt', wert: raum.lichtstromGesamtLm, einheit: 'lm' },
              { beschriftung: 'Anschlussleistung', wert: raum.anschlussleistungW, einheit: 'W', nachkomma: 1 },
              {
                beschriftung: 'spezifische Leistung', wert: raum.spezifischeLeistungWm2,
                einheit: 'W/m²', nachkomma: 1,
              },
              {
                beschriftung: 'Sicherheitsleuchten', wert: raum.notleuchtenAnzahl, einheit: 'Stück',
                hinweis: 'gezählt, nicht gerechnet', herkunft: 'modell',
              },

              {
                beschriftung: 'UGR', wert: raum.ugrGrenze,
                hinweis: `Grenze der Norm → ${raum.ugrAussage}`,
              },
              {
                beschriftung: 'Ra', wert: raum.raMindest,
                hinweis: `Mindestwert der Norm → ${raum.raAussage}`,
              },
              {
                beschriftung: 'U0 gefordert', wert: raum.gleichmaessigkeitSoll, nachkomma: 2,
                hinweis: 'nicht gerechnet — braucht eine Punktrechnung',
              },
            ]}
          />
        </ContentSection>
      )}
    </>
  );
}
